import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  Alert,
  Divider,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  Grid
} from '@mui/material';
import {
  FolderKanban,
  Plus,
  Edit,
  Trash2,
  Save,
  RefreshCw,
  Layout,
  Clock,
  DollarSign,
  Package,
  Repeat,
  GripVertical,
  Check,
  X,
  ChevronRight,
  Code,
  Shield,
  Phone,
  MoreHorizontal,
  Sparkles
} from 'lucide-react';

// ============================================
// PROJECT TYPES TAB
// ============================================

function ProjectTypesTab({ onSelectType }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const fetchTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch('/api/project-types?includeInactive=true', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTypes(data);
      }
    } catch (err) {
      console.error('Fetch types error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  const handleOpenDialog = (type = null) => {
    setEditingType(type ? { ...type } : { slug: '', name: '', description: '', color: '#48bdcb' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('portalToken');
    const isNew = !editingType.id;
    
    try {
      const response = await fetch(
        isNew ? '/api/project-types' : `/api/project-types/${editingType.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editingType)
        }
      );
      
      if (response.ok) {
        fetchTypes();
        setDialogOpen(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Save type error:', err);
    }
  };

  const handleToggleActive = async (type) => {
    const token = localStorage.getItem('portalToken');
    try {
      await fetch(`/api/project-types/${type.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !type.isActive })
      });
      fetchTypes();
    } catch (err) {
      console.error('Toggle active error:', err);
    }
  };

  const getTypeIcon = (slug) => {
    switch (slug) {
      case 'dev': return <Code size={24} />;
      case 'cybersecurity': return <Shield size={24} />;
      case 'voip': return <Phone size={24} />;
      default: return <FolderKanban size={24} />;
    }
  };

  if (loading) {
    return <Typography sx={{ p: 3 }}>Chargement...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Gérez les différents types de projets disponibles pour les devis
        </Typography>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => handleOpenDialog()}>
          Nouveau type
        </Button>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>Type de projet enregistré avec succès</Alert>
      )}

      <Grid container spacing={3}>
        {types.map((type) => (
          <Grid item xs={12} md={4} key={type.id}>
            <Card 
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.02)', 
                border: `1px solid ${type.isActive ? type.color + '40' : 'rgba(255,255,255,0.06)'}`,
                opacity: type.isActive ? 1 : 0.6,
                transition: 'all 0.2s'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: type.color + '20',
                    color: type.color,
                    display: 'flex'
                  }}>
                    {getTypeIcon(type.slug)}
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch 
                        checked={type.isActive} 
                        onChange={() => handleToggleActive(type)}
                        size="small"
                      />
                    }
                    label=""
                  />
                </Box>
                
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {type.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  {type.slug}
                </Typography>
                
                {type.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {type.description}
                  </Typography>
                )}

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip 
                    icon={<Layout size={12} />} 
                    label={`${type._count?.categories || 0} catégories`} 
                    size="small" 
                    variant="outlined"
                  />
                  <Chip 
                    icon={<Clock size={12} />} 
                    label={`${type._count?.hourTypes || 0} types h.`} 
                    size="small" 
                    variant="outlined"
                  />
                  <Chip 
                    icon={<Package size={12} />} 
                    label={`${type._count?.products || 0} produits`} 
                    size="small" 
                    variant="outlined"
                  />
                </Stack>
              </CardContent>
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button 
                  size="small" 
                  startIcon={<Edit size={14} />}
                  onClick={() => handleOpenDialog(type)}
                >
                  Modifier
                </Button>
                <Button 
                  size="small" 
                  endIcon={<ChevronRight size={14} />}
                  onClick={() => onSelectType(type)}
                  sx={{ color: type.color }}
                >
                  Configurer
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingType?.id ? 'Modifier le type' : 'Nouveau type de projet'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Slug (identifiant)"
              value={editingType?.slug || ''}
              onChange={(e) => setEditingType(prev => ({ 
                ...prev, 
                slug: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') 
              }))}
              helperText="Ex: dev, cybersecurity, voip"
              disabled={!!editingType?.id}
            />
            <TextField
              fullWidth
              label="Nom"
              value={editingType?.name || ''}
              onChange={(e) => setEditingType(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={editingType?.description || ''}
              onChange={(e) => setEditingType(prev => ({ ...prev, description: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Couleur"
              type="color"
              value={editingType?.color || '#48bdcb'}
              onChange={(e) => setEditingType(prev => ({ ...prev, color: e.target.value }))}
              InputProps={{
                startAdornment: (
                  <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: editingType?.color, mr: 1 }} />
                )
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={!editingType?.slug || !editingType?.name}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================
// CATEGORIES TAB
// ============================================

function CategoriesTab({ projectType }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchCategories = useCallback(async () => {
    if (!projectType?.id) return;
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/project-types/${projectType.id}/categories?includeInactive=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setCategories(await response.json());
      }
    } catch (err) {
      console.error('Fetch categories error:', err);
    }
    setLoading(false);
  }, [projectType?.id]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleOpenDialog = (item = null) => {
    setEditingItem(item ? { ...item } : { slug: '', name: '', icon: 'Layout', description: '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('portalToken');
    const isNew = !editingItem.id;
    
    try {
      const response = await fetch(
        isNew 
          ? `/api/project-types/${projectType.id}/categories`
          : `/api/project-types/${projectType.id}/categories/${editingItem.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editingItem)
        }
      );
      
      if (response.ok) {
        fetchCategories();
        setDialogOpen(false);
      }
    } catch (err) {
      console.error('Save category error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    const token = localStorage.getItem('portalToken');
    try {
      await fetch(`/api/project-types/${projectType.id}/categories/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchCategories();
    } catch (err) {
      console.error('Delete category error:', err);
    }
  };

  if (loading) return <Typography>Chargement...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Catégories de sections pour les devis {projectType?.name}
        </Typography>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => handleOpenDialog()}>
          Nouvelle catégorie
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={50}></TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Icône</TableCell>
              <TableCell align="center">Utilisations</TableCell>
              <TableCell align="center">Actif</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.map((cat) => (
              <TableRow key={cat.id} hover sx={{ opacity: cat.isActive ? 1 : 0.5 }}>
                <TableCell>
                  <GripVertical size={16} style={{ opacity: 0.3, cursor: 'grab' }} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {cat.name}
                    {cat.isOther && <Chip label="Autre" size="small" sx={{ ml: 1, fontSize: '0.65rem' }} />}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">{cat.slug}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{cat.icon || '-'}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip label={cat._count?.sections || 0} size="small" />
                </TableCell>
                <TableCell align="center">
                  {cat.isActive ? <Check size={16} color="#4caf50" /> : <X size={16} color="#f44336" />}
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(cat)} disabled={cat.isOther}>
                    <Edit size={16} />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(cat.id)} color="error" disabled={cat.isOther}>
                    <Trash2 size={16} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem?.id ? 'Modifier' : 'Nouvelle catégorie'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Slug"
              value={editingItem?.slug || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
              disabled={!!editingItem?.id}
            />
            <TextField
              fullWidth
              label="Nom"
              value={editingItem?.name || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Icône (Lucide)"
              value={editingItem?.icon || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, icon: e.target.value }))}
              helperText="Ex: Layout, Zap, Database, Shield"
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={editingItem?.description || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, description: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={!editingItem?.slug || !editingItem?.name}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================
// HOUR TYPES TAB
// ============================================

function HourTypesTab({ projectType }) {
  const [hourTypes, setHourTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchHourTypes = useCallback(async () => {
    if (!projectType?.id) return;
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/project-types/${projectType.id}/hour-types?includeInactive=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setHourTypes(await response.json());
      }
    } catch (err) {
      console.error('Fetch hour types error:', err);
    }
    setLoading(false);
  }, [projectType?.id]);

  useEffect(() => {
    fetchHourTypes();
  }, [fetchHourTypes]);

  const handleOpenDialog = (item = null) => {
    setEditingItem(item ? { ...item } : { slug: '', name: '', defaultRate: null, description: '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('portalToken');
    const isNew = !editingItem.id;
    
    try {
      const response = await fetch(
        isNew 
          ? `/api/project-types/${projectType.id}/hour-types`
          : `/api/project-types/${projectType.id}/hour-types/${editingItem.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editingItem)
        }
      );
      
      if (response.ok) {
        fetchHourTypes();
        setDialogOpen(false);
      }
    } catch (err) {
      console.error('Save hour type error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce type d\'heures ?')) return;
    const token = localStorage.getItem('portalToken');
    try {
      await fetch(`/api/project-types/${projectType.id}/hour-types/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchHourTypes();
    } catch (err) {
      console.error('Delete hour type error:', err);
    }
  };

  if (loading) return <Typography>Chargement...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Types d'heures pour les devis {projectType?.name}
        </Typography>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => handleOpenDialog()}>
          Nouveau type d'heures
        </Button>
      </Box>

      <Stack spacing={2}>
        {hourTypes.map((ht) => (
          <Paper
            key={ht.id}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              opacity: ht.isActive ? 1 : 0.5
            }}
          >
            <Box sx={{ p: 1, borderRadius: 1, bgcolor: `${projectType?.color || '#48bdcb'}20` }}>
              <Clock size={20} color={projectType?.color || '#48bdcb'} />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{ht.name}</Typography>
              <Typography variant="caption" color="text.secondary">{ht.slug}</Typography>
            </Box>
            {ht.defaultRate && (
              <Chip 
                icon={<DollarSign size={12} />}
                label={`${ht.defaultRate}$/h`} 
                size="small" 
                variant="outlined"
              />
            )}
            <Box>
              <IconButton size="small" onClick={() => handleOpenDialog(ht)}>
                <Edit size={16} />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(ht.id)} color="error">
                <Trash2 size={16} />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Stack>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem?.id ? 'Modifier' : 'Nouveau type d\'heures'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Slug"
              value={editingItem?.slug || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
              disabled={!!editingItem?.id}
            />
            <TextField
              fullWidth
              label="Nom"
              value={editingItem?.name || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Taux par défaut (optionnel)"
              type="number"
              value={editingItem?.defaultRate || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, defaultRate: e.target.value ? parseFloat(e.target.value) : null }))}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                endAdornment: <InputAdornment position="end">/h</InputAdornment>
              }}
              helperText="Laissez vide pour utiliser le taux global"
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={editingItem?.description || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, description: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={!editingItem?.slug || !editingItem?.name}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================
// PRODUCTS TAB
// ============================================

function ProductsTab({ projectType }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchProducts = useCallback(async () => {
    if (!projectType?.id) return;
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/project-types/${projectType.id}/products?includeInactive=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setProducts(await response.json());
      }
    } catch (err) {
      console.error('Fetch products error:', err);
    }
    setLoading(false);
  }, [projectType?.id]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleOpenDialog = (item = null) => {
    setEditingItem(item ? { ...item } : { sku: '', name: '', description: '', unitPrice: 0, unit: 'unité' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('portalToken');
    const isNew = !editingItem.id;
    
    try {
      const response = await fetch(
        isNew 
          ? `/api/project-types/${projectType.id}/products`
          : `/api/project-types/${projectType.id}/products/${editingItem.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editingItem)
        }
      );
      
      if (response.ok) {
        fetchProducts();
        setDialogOpen(false);
      }
    } catch (err) {
      console.error('Save product error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    const token = localStorage.getItem('portalToken');
    try {
      await fetch(`/api/project-types/${projectType.id}/products/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchProducts();
    } catch (err) {
      console.error('Delete product error:', err);
    }
  };

  if (loading) return <Typography>Chargement...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Catalogue de produits/matériel pour {projectType?.name}
        </Typography>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => handleOpenDialog()}>
          Nouveau produit
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>SKU</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Prix unitaire</TableCell>
              <TableCell>Unité</TableCell>
              <TableCell align="center">Utilisations</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((prod) => (
              <TableRow key={prod.id} hover sx={{ opacity: prod.isActive ? 1 : 0.5 }}>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{prod.sku || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{prod.name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" sx={{ 
                    maxWidth: 200, 
                    display: 'block', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap' 
                  }}>
                    {prod.description || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontWeight: 600, color: '#00d4aa' }}>
                    ${prod.unitPrice?.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>{prod.unit}</TableCell>
                <TableCell align="center">
                  <Chip label={prod._count?.quoteProducts || 0} size="small" />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(prod)}>
                    <Edit size={16} />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(prod.id)} color="error">
                    <Trash2 size={16} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {products.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Aucun produit dans le catalogue</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem?.id ? 'Modifier' : 'Nouveau produit'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="SKU (optionnel)"
              value={editingItem?.sku || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, sku: e.target.value.toUpperCase() }))}
              helperText="Code produit unique"
            />
            <TextField
              fullWidth
              label="Nom"
              value={editingItem?.name || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={editingItem?.description || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, description: e.target.value }))}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Prix unitaire"
                type="number"
                value={editingItem?.unitPrice || 0}
                onChange={(e) => setEditingItem(prev => ({ ...prev, unitPrice: parseFloat(e.target.value) || 0 }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
              <TextField
                fullWidth
                label="Unité"
                value={editingItem?.unit || 'unité'}
                onChange={(e) => setEditingItem(prev => ({ ...prev, unit: e.target.value }))}
                helperText="Ex: unité, licence, poste"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={!editingItem?.name}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================
// RECURRING COST TYPES TAB
// ============================================

function RecurringCostTypesTab({ projectType }) {
  const [costTypes, setCostTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const fetchCostTypes = useCallback(async () => {
    if (!projectType?.id) return;
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/project-types/${projectType.id}/recurring-cost-types?includeInactive=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setCostTypes(await response.json());
      }
    } catch (err) {
      console.error('Fetch cost types error:', err);
    }
    setLoading(false);
  }, [projectType?.id]);

  useEffect(() => {
    fetchCostTypes();
  }, [fetchCostTypes]);

  const handleOpenDialog = (item = null) => {
    setEditingItem(item ? { ...item } : { slug: '', name: '', description: '', defaultCost: 0 });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('portalToken');
    const isNew = !editingItem.id;
    
    try {
      const response = await fetch(
        isNew 
          ? `/api/project-types/${projectType.id}/recurring-cost-types`
          : `/api/project-types/${projectType.id}/recurring-cost-types/${editingItem.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editingItem)
        }
      );
      
      if (response.ok) {
        fetchCostTypes();
        setDialogOpen(false);
      }
    } catch (err) {
      console.error('Save cost type error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce type de coût ?')) return;
    const token = localStorage.getItem('portalToken');
    try {
      await fetch(`/api/project-types/${projectType.id}/recurring-cost-types/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchCostTypes();
    } catch (err) {
      console.error('Delete cost type error:', err);
    }
  };

  if (loading) return <Typography>Chargement...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Types de coûts récurrents pour {projectType?.name}
        </Typography>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => handleOpenDialog()}>
          Nouveau type
        </Button>
      </Box>

      <Stack spacing={2}>
        {costTypes.map((ct) => (
          <Paper
            key={ct.id}
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              opacity: ct.isActive ? 1 : 0.5
            }}
          >
            <Box sx={{ p: 1, borderRadius: 1, bgcolor: 'rgba(249, 115, 22, 0.1)' }}>
              <Repeat size={20} color="#f97316" />
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{ct.name}</Typography>
              <Typography variant="caption" color="text.secondary">{ct.slug}</Typography>
            </Box>
            {ct.defaultCost > 0 && (
              <Chip 
                label={`${ct.defaultCost}$/mois`} 
                size="small" 
                sx={{ bgcolor: 'rgba(249, 115, 22, 0.15)', color: '#f97316', fontWeight: 600 }}
              />
            )}
            <Box>
              <IconButton size="small" onClick={() => handleOpenDialog(ct)}>
                <Edit size={16} />
              </IconButton>
              <IconButton size="small" onClick={() => handleDelete(ct.id)} color="error">
                <Trash2 size={16} />
              </IconButton>
            </Box>
          </Paper>
        ))}
        {costTypes.length === 0 && (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'rgba(255,255,255,0.02)' }}>
            <Typography color="text.secondary">Aucun type de coût récurrent</Typography>
          </Paper>
        )}
      </Stack>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem?.id ? 'Modifier' : 'Nouveau type de coût'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Slug"
              value={editingItem?.slug || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
              disabled={!!editingItem?.id}
            />
            <TextField
              fullWidth
              label="Nom"
              value={editingItem?.name || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={editingItem?.description || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, description: e.target.value }))}
            />
            <TextField
              fullWidth
              type="number"
              label="Coût mensuel par défaut"
              value={editingItem?.defaultCost || 0}
              onChange={(e) => setEditingItem(prev => ({ ...prev, defaultCost: parseFloat(e.target.value) || 0 }))}
              InputProps={{
                startAdornment: <InputAdornment position="start"><DollarSign size={16} /></InputAdornment>,
                endAdornment: <InputAdornment position="end">/mois</InputAdornment>
              }}
              inputProps={{ min: 0, step: 5 }}
              helperText="Ce montant sera pré-rempli lors de l'ajout d'un coût récurrent dans un devis"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={!editingItem?.slug || !editingItem?.name}>
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================
// HOURLY RATES TAB
// ============================================

function HourlyRatesTab({ projectType }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [originalRates, setOriginalRates] = useState({});

  const fetchCategories = useCallback(async () => {
    if (!projectType?.id) return;
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/project-types/${projectType.id}/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
        // Store original rates
        const rates = {};
        data.forEach(cat => { rates[cat.id] = cat.hourlyRate || 150; });
        setOriginalRates(rates);
      }
    } catch (err) {
      console.error('Fetch categories error:', err);
    }
    setLoading(false);
  }, [projectType?.id]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleRateChange = (categoryId, newRate) => {
    const numRate = parseFloat(newRate) || 0;
    setCategories(prev => prev.map(cat => 
      cat.id === categoryId ? { ...cat, hourlyRate: numRate } : cat
    ));
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('portalToken');
      const rates = categories.map(cat => ({
        categoryId: cat.id,
        hourlyRate: cat.hourlyRate || 150
      }));
      
      const response = await fetch(`/api/project-types/${projectType.id}/categories/rates`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ rates })
      });

      if (response.ok) {
        const newRates = {};
        categories.forEach(cat => { newRates[cat.id] = cat.hourlyRate; });
        setOriginalRates(newRates);
        setHasChanges(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Save rates error:', err);
    }
    setSaving(false);
  };

  const handleReset = () => {
    setCategories(prev => prev.map(cat => ({
      ...cat,
      hourlyRate: originalRates[cat.id] || 150
    })));
    setHasChanges(false);
  };

  const avgRate = categories.length > 0 
    ? categories.reduce((sum, cat) => sum + (cat.hourlyRate || 150), 0) / categories.length 
    : 150;

  if (loading) {
    return <Typography sx={{ p: 3 }}>Chargement des taux...</Typography>;
  }

  return (
    <Box>
      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Stack direction="row" spacing={1}>
          {hasChanges && (
            <Button
              variant="outlined"
              startIcon={<RefreshCw size={16} />}
              onClick={handleReset}
              color="inherit"
            >
              Annuler
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Save size={16} />}
            onClick={handleSaveAll}
            disabled={!hasChanges || saving}
            sx={{ bgcolor: projectType?.color }}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Stack>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Les taux horaires ont été mis à jour avec succès
        </Alert>
      )}

      {/* Stats */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: `${projectType?.color}10`, border: `1px solid ${projectType?.color}30`, borderRadius: 2 }}>
        <Stack direction="row" spacing={4} alignItems="center">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: `${projectType?.color}20` }}>
              <DollarSign size={24} color={projectType?.color} />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Taux moyen</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: projectType?.color }}>
                ${avgRate.toFixed(0)}/h
              </Typography>
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem sx={{ opacity: 0.2 }} />
          <Box>
            <Typography variant="caption" color="text.secondary">Catégories</Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{categories.length}</Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Rates List */}
      <Stack spacing={2}>
        {categories.map((cat) => {
          const isChanged = (cat.hourlyRate || 150) !== (originalRates[cat.id] || 150);
          
          return (
            <Paper
              key={cat.id}
              sx={{
                p: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                bgcolor: 'rgba(255, 255, 255, 0.02)',
                border: isChanged ? `1px solid ${projectType?.color}50` : '1px solid rgba(255, 255, 255, 0.06)',
                borderRadius: 2,
                transition: 'all 0.2s'
              }}
            >
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 1.5, 
                bgcolor: `${projectType?.color}15`,
                color: projectType?.color,
                display: 'flex'
              }}>
                <Layout size={20} />
              </Box>

              <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {cat.name}
                  </Typography>
                  {cat.isOther && (
                    <Chip label="Autre" size="small" sx={{ height: 20, fontSize: '0.65rem' }} />
                  )}
                  {isChanged && (
                    <Chip 
                      label="Modifié" 
                      size="small" 
                      sx={{ height: 20, fontSize: '0.65rem', bgcolor: `${projectType?.color}20`, color: projectType?.color }} 
                    />
                  )}
                </Box>
                {cat.description && (
                  <Typography variant="body2" color="text.secondary">
                    {cat.description}
                  </Typography>
                )}
              </Box>

              <TextField
                size="small"
                type="number"
                value={cat.hourlyRate || 150}
                onChange={(e) => handleRateChange(cat.id, e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><DollarSign size={16} /></InputAdornment>,
                  endAdornment: <InputAdornment position="end">/h</InputAdornment>
                }}
                inputProps={{ min: 0, step: 5 }}
                sx={{ 
                  width: 150,
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': { borderColor: projectType?.color }
                  }
                }}
              />
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
}

// ============================================
// ESTIMATES TAB
// ============================================

function EstimatesTab({ projectType }) {
  const [estimates, setEstimates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hourTypes, setHourTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Comparative view with averages
  const [showAverages, setShowAverages] = useState(false);
  const [averagesData, setAveragesData] = useState([]);

  const emptyItem = {
    slug: '',
    name: '',
    description: '',
    categoryId: '',
    hoursBreakdown: {},
    complexity: 'MEDIUM'
  };

  const complexityConfig = {
    LOW: { label: 'Simple', color: '#4caf50' },
    MEDIUM: { label: 'Moyen', color: '#ff9800' },
    HIGH: { label: 'Complexe', color: '#f44336' },
    COMPLEX: { label: 'Très complexe', color: '#9c27b0' }
  };

  const fetchData = useCallback(async () => {
    if (!projectType?.id) return;
    try {
      const token = localStorage.getItem('portalToken');
      
      // Fetch estimates
      const estRes = await fetch(`/api/project-types/${projectType.id}/estimates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (estRes.ok) {
        setEstimates(await estRes.json());
      }
      
      // Fetch categories
      const catRes = await fetch(`/api/project-types/${projectType.id}/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (catRes.ok) {
        setCategories(await catRes.json());
      }
      
      // Fetch hour types
      const htRes = await fetch(`/api/project-types/${projectType.id}/hour-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (htRes.ok) {
        setHourTypes(await htRes.json());
      }
    } catch (err) {
      console.error('Fetch estimates error:', err);
    }
    setLoading(false);
  }, [projectType?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch averages when toggle is enabled
  const fetchAverages = useCallback(async () => {
    if (!projectType?.id || !showAverages) return;
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/project-types/${projectType.id}/estimates/averages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAveragesData(await response.json());
      }
    } catch (err) {
      console.error('Fetch averages error:', err);
    }
  }, [projectType?.id, showAverages]);

  useEffect(() => {
    if (showAverages) {
      fetchAverages();
    }
  }, [showAverages, fetchAverages]);

  // Helper to get average for an estimate and hour type
  const getAverage = (estimateId, hourTypeId) => {
    const avgEntry = averagesData.find(a => a.id === estimateId);
    if (!avgEntry) return null;
    const htAvg = avgEntry.hourTypes?.[hourTypeId];
    return htAvg?.average ?? null;
  };

  // Helper to get sample count for an estimate
  const getSampleCount = (estimateId) => {
    const avgEntry = averagesData.find(a => a.id === estimateId);
    return avgEntry?.sampleCount || 0;
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem({ ...item });
    } else {
      // Initialize hoursBreakdown with all hour types at 0
      const breakdown = {};
      hourTypes.forEach(ht => { breakdown[ht.slug] = 0; });
      setEditingItem({ ...emptyItem, hoursBreakdown: breakdown });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
  };

  const handleSaveItem = async () => {
    const token = localStorage.getItem('portalToken');
    const isNew = !editingItem.id;
    
    try {
      const response = await fetch(
        isNew 
          ? `/api/project-types/${projectType.id}/estimates`
          : `/api/project-types/${projectType.id}/estimates/${editingItem.id}`,
        {
          method: isNew ? 'POST' : 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editingItem)
        }
      );
      
      if (response.ok) {
        fetchData();
        handleCloseDialog();
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Save estimate error:', err);
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Supprimer cette estimation ?')) return;
    
    const token = localStorage.getItem('portalToken');
    try {
      const response = await fetch(`/api/project-types/${projectType.id}/estimates/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Delete estimate error:', err);
    }
  };

  const handleHoursChange = (slug, value) => {
    setEditingItem(prev => ({
      ...prev,
      hoursBreakdown: {
        ...prev.hoursBreakdown,
        [slug]: parseFloat(value) || 0
      }
    }));
  };

  const getTotalHours = (item) => {
    if (!item.hoursBreakdown) return 0;
    return Object.values(item.hoursBreakdown).reduce((sum, h) => sum + (h || 0), 0);
  };

  const filteredEstimates = filterCategory 
    ? estimates.filter(e => e.categoryId === filterCategory)
    : estimates;

  if (loading) {
    return <Typography sx={{ p: 3 }}>Chargement des estimations...</Typography>;
  }

  return (
    <Box>
      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filtrer par catégorie</InputLabel>
          <Select
            value={filterCategory}
            label="Filtrer par catégorie"
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <MenuItem value="">Toutes les catégories</MenuItem>
            {categories.map(cat => (
              <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Button
          variant="contained"
          startIcon={<Plus size={16} />}
          onClick={() => handleOpenDialog()}
          sx={{ bgcolor: projectType?.color }}
        >
          Nouvelle estimation
        </Button>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          L'estimation a été enregistrée avec succès
        </Alert>
      )}

      {/* Stats */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: `${projectType?.color}10`, border: `1px solid ${projectType?.color}30`, borderRadius: 2 }}>
        <Stack direction="row" spacing={4} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={4} alignItems="center">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: `${projectType?.color}20` }}>
                <Clock size={24} color={projectType?.color} />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Estimations configurées</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: projectType?.color }}>
                  {estimates.length}
                </Typography>
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ opacity: 0.2 }} />
            <Box>
              <Typography variant="caption" color="text.secondary">Types d'heures</Typography>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{hourTypes.length}</Typography>
            </Box>
          </Stack>
          
          {/* Comparative view toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={showAverages}
                onChange={(e) => setShowAverages(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">Vue comparative</Typography>
                <Tooltip title="Affiche les moyennes réelles à côté des estimations">
                  <span style={{ cursor: 'help', opacity: 0.7 }}>ⓘ</span>
                </Tooltip>
              </Box>
            }
          />
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Catégorie</TableCell>
              <TableCell>Nom</TableCell>
              {hourTypes.slice(0, 5).map(ht => (
                showAverages ? (
                  <React.Fragment key={ht.id}>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)' }}>
                      <Tooltip title={`${ht.name} - Estimé`}><span>{ht.name.substring(0, 3)} Est.</span></Tooltip>
                    </TableCell>
                    <TableCell align="center" sx={{ bgcolor: 'rgba(0, 212, 170, 0.05)' }}>
                      <Tooltip title={`${ht.name} - Moyenne réelle`}><span>{ht.name.substring(0, 3)} Moy.</span></Tooltip>
                    </TableCell>
                  </React.Fragment>
                ) : (
                  <TableCell key={ht.id} align="center">
                    <Tooltip title={ht.name}><span>{ht.name.substring(0, 3)}</span></Tooltip>
                  </TableCell>
                )
              ))}
              <TableCell align="center">Total</TableCell>
              {showAverages && <TableCell align="center">Échantillons</TableCell>}
              <TableCell align="center">Complexité</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEstimates.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>
                  <Chip 
                    label={item.category?.name || '-'} 
                    size="small" 
                    sx={{ fontSize: '0.7rem' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {item.slug}
                  </Typography>
                </TableCell>
                {hourTypes.slice(0, 5).map(ht => {
                  const estimated = item.hoursBreakdown?.[ht.slug] || 0;
                  const average = getAverage(item.id, ht.id);
                  
                  return showAverages ? (
                    <React.Fragment key={ht.id}>
                      <TableCell align="center" sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)' }}>
                        {estimated || '-'}
                      </TableCell>
                      <TableCell align="center" sx={{ bgcolor: 'rgba(0, 212, 170, 0.05)' }}>
                        {average !== null ? (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 500,
                              color: average > estimated ? '#ef4444' : average < estimated ? '#10b981' : '#9ca3af'
                            }}
                          >
                            {average}
                          </Typography>
                        ) : '-'}
                      </TableCell>
                    </React.Fragment>
                  ) : (
                    <TableCell key={ht.id} align="center">
                      {estimated || '-'}
                    </TableCell>
                  );
                })}
                <TableCell align="center">
                  <Typography sx={{ fontWeight: 'bold', color: projectType?.color }}>
                    {getTotalHours(item)}h
                  </Typography>
                </TableCell>
                {showAverages && (
                  <TableCell align="center">
                    <Chip 
                      label={getSampleCount(item.id)} 
                      size="small" 
                      sx={{ 
                        fontSize: '0.65rem',
                        bgcolor: getSampleCount(item.id) > 0 ? 'rgba(0, 212, 170, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                        color: getSampleCount(item.id) > 0 ? '#00d4aa' : '#9ca3af'
                      }}
                    />
                  </TableCell>
                )}
                <TableCell align="center">
                  <Chip
                    label={complexityConfig[item.complexity]?.label || item.complexity}
                    size="small"
                    sx={{ 
                      fontSize: '0.65rem',
                      bgcolor: `${complexityConfig[item.complexity]?.color}20`,
                      color: complexityConfig[item.complexity]?.color
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                    <Edit size={16} />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteItem(item.id)} color="error">
                    <Trash2 size={16} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredEstimates.length === 0 && (
              <TableRow>
                <TableCell 
                  colSpan={showAverages ? 6 + (hourTypes.slice(0, 5).length * 2) : 6 + hourTypes.slice(0, 5).length} 
                  align="center" 
                  sx={{ py: 4 }}
                >
                  <Typography color="text.secondary">Aucune estimation configurée</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingItem?.id ? 'Modifier l\'estimation' : 'Nouvelle estimation'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Slug (identifiant unique)"
                value={editingItem?.slug || ''}
                onChange={(e) => setEditingItem(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                helperText="Ex: page_simple, crud_complet"
                disabled={!!editingItem?.id}
              />
              <FormControl fullWidth>
                <InputLabel>Catégorie</InputLabel>
                <Select
                  value={editingItem?.categoryId || ''}
                  label="Catégorie"
                  onChange={(e) => setEditingItem(prev => ({ ...prev, categoryId: e.target.value }))}
                >
                  <MenuItem value="">Aucune</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            
            <TextField
              fullWidth
              label="Nom"
              value={editingItem?.name || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, name: e.target.value }))}
            />
            
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={editingItem?.description || ''}
              onChange={(e) => setEditingItem(prev => ({ ...prev, description: e.target.value }))}
            />

            <Typography variant="subtitle2" sx={{ pt: 1 }}>Heures estimées par type</Typography>
            
            <Grid container spacing={2}>
              {hourTypes.map(ht => (
                <Grid item xs={6} sm={4} md={2.4} key={ht.id}>
                  <TextField
                    fullWidth
                    label={ht.name}
                    type="number"
                    size="small"
                    value={editingItem?.hoursBreakdown?.[ht.slug] || 0}
                    onChange={(e) => handleHoursChange(ht.slug, e.target.value)}
                    inputProps={{ min: 0, step: 0.5 }}
                  />
                </Grid>
              ))}
            </Grid>

            <FormControl fullWidth>
              <InputLabel>Complexité</InputLabel>
              <Select
                value={editingItem?.complexity || 'MEDIUM'}
                label="Complexité"
                onChange={(e) => setEditingItem(prev => ({ ...prev, complexity: e.target.value }))}
              >
                {Object.entries(complexityConfig).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Chip
                      label={config.label}
                      size="small"
                      sx={{ bgcolor: `${config.color}20`, color: config.color }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {editingItem && (
              <Paper sx={{ p: 2, bgcolor: `${projectType?.color}10`, border: `1px solid ${projectType?.color}30` }}>
                <Typography variant="body2" color="text.secondary">
                  Total estimé: <strong style={{ color: projectType?.color }}>{getTotalHours(editingItem)}h</strong>
                </Typography>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annuler</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveItem}
            disabled={!editingItem?.slug || !editingItem?.name}
            sx={{ bgcolor: projectType?.color }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ============================================
// AI PROMPT TAB
// ============================================

function AiPromptTab({ projectType }) {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fetchPrompt = useCallback(async () => {
    if (!projectType?.id) return;
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/project-types/${projectType.id}/ai-prompt`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPrompt(data.aiTemplatePrompt || '');
        setHasChanges(false);
      }
    } catch (err) {
      console.error('Fetch AI prompt error:', err);
    }
    setLoading(false);
  }, [projectType?.id]);

  useEffect(() => {
    fetchPrompt();
  }, [fetchPrompt]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('portalToken');
      const response = await fetch(`/api/project-types/${projectType.id}/ai-prompt`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ aiTemplatePrompt: prompt })
      });
      if (response.ok) {
        setSaveSuccess(true);
        setHasChanges(false);
        setTimeout(() => setSaveSuccess(false), 2000);
      }
    } catch (err) {
      console.error('Save AI prompt error:', err);
    }
    setSaving(false);
  };

  const handleReset = () => {
    if (window.confirm('Réinitialiser le prompt ? Cette action supprimera le prompt personnalisé et utilisera le prompt par défaut.')) {
      setPrompt('');
      setHasChanges(true);
    }
  };

  if (loading) return <Typography>Chargement...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Personnalisez les instructions AI pour la génération de templates de devis pour ce type de projet.
            Si vide, le prompt par défaut de l'agent template-builder sera utilisé.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {prompt && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleReset}
            >
              Réinitialiser
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<Save size={16} />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
            sx={{ bgcolor: projectType?.color }}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </Stack>
      </Box>

      {saveSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Prompt AI enregistré avec succès
        </Alert>
      )}

      <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Code size={16} />
          Variables disponibles
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {[
            { var: '{{SECTION_TYPES}}', desc: 'Catégories du type' },
            { var: '{{HOUR_TYPES}}', desc: 'Types d\'heures' },
            { var: '{{HOURLY_RATES}}', desc: 'Taux horaires par catégorie' },
            { var: '{{RECURRING_COST_TYPES}}', desc: 'Types de coûts récurrents' },
            { var: '{{HOUR_ESTIMATES_TABLE}}', desc: 'Table des estimations d\'heures' }
          ].map(v => (
            <Tooltip key={v.var} title={v.desc}>
              <Chip 
                label={v.var} 
                size="small" 
                sx={{ 
                  fontFamily: 'monospace', 
                  fontSize: '0.75rem',
                  bgcolor: `${projectType?.color}15`,
                  color: projectType?.color
                }} 
              />
            </Tooltip>
          ))}
        </Stack>
      </Paper>

      <TextField
        fullWidth
        multiline
        rows={20}
        label="Prompt AI personnalisé"
        value={prompt}
        onChange={(e) => {
          setPrompt(e.target.value);
          setHasChanges(true);
        }}
        placeholder={`Entrez les instructions personnalisées pour l'AI...

Exemple pour ${projectType?.name}:

Tu es un générateur de templates de devis spécialisé en ${projectType?.name}.

=== CONTEXTE ===
Types de sections : {{SECTION_TYPES}}
Types d'heures : {{HOUR_TYPES}}
Taux horaires : {{HOURLY_RATES}}

=== INSTRUCTIONS SPÉCIFIQUES ===
- ...
- ...`}
        sx={{
          '& .MuiInputBase-root': {
            fontFamily: 'monospace',
            fontSize: '0.875rem'
          }
        }}
      />

      <Paper sx={{ p: 2, mt: 2, bgcolor: 'rgba(245, 158, 11, 0.05)', border: '1px solid rgba(245, 158, 11, 0.2)' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Note :</strong> Ce prompt sera injecté lors de l'utilisation du générateur AI de templates 
          pour ce type de projet. Laissez vide pour utiliser le prompt générique.
        </Typography>
      </Paper>
    </Box>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

function ProjectTypeSettings() {
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedType, setSelectedType] = useState(null);

  const handleSelectType = (type) => {
    setSelectedType(type);
    setCurrentTab(0); // Go to categories tab
  };

  const handleBackToTypes = () => {
    setSelectedType(null);
    setCurrentTab(0);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
          {selectedType && (
            <Button 
              size="small" 
              onClick={handleBackToTypes}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              ← Retour
            </Button>
          )}
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            {selectedType ? selectedType.name : 'Types de projets'}
          </Typography>
          {selectedType && (
            <Box sx={{ 
              width: 12, 
              height: 12, 
              borderRadius: '50%', 
              bgcolor: selectedType.color 
            }} />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {selectedType 
            ? 'Configurez les catégories, types d\'heures, produits et coûts récurrents'
            : 'Gérez les différents types de projets et leurs configurations'
          }
        </Typography>
      </Box>

      {/* Tabs */}
      {selectedType ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={currentTab} 
              onChange={(e, newValue) => setCurrentTab(newValue)}
              sx={{
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
                '& .Mui-selected': { color: selectedType.color },
                '& .MuiTabs-indicator': { backgroundColor: selectedType.color }
              }}
            >
              <Tab icon={<Layout size={18} />} iconPosition="start" label="Catégories" />
              <Tab icon={<DollarSign size={18} />} iconPosition="start" label="Taux horaires" />
              <Tab icon={<Clock size={18} />} iconPosition="start" label="Types d'heures" />
              <Tab icon={<Package size={18} />} iconPosition="start" label="Produits" />
              <Tab icon={<Repeat size={18} />} iconPosition="start" label="Coûts récurrents" />
              <Tab icon={<Clock size={18} />} iconPosition="start" label="Estimations" />
              <Tab icon={<Sparkles size={18} />} iconPosition="start" label="Prompt AI" />
            </Tabs>
          </Box>

          {currentTab === 0 && <CategoriesTab projectType={selectedType} />}
          {currentTab === 1 && <HourlyRatesTab projectType={selectedType} />}
          {currentTab === 2 && <HourTypesTab projectType={selectedType} />}
          {currentTab === 3 && <ProductsTab projectType={selectedType} />}
          {currentTab === 4 && <RecurringCostTypesTab projectType={selectedType} />}
          {currentTab === 5 && <EstimatesTab projectType={selectedType} />}
          {currentTab === 6 && <AiPromptTab projectType={selectedType} />}
        </>
      ) : (
        <ProjectTypesTab onSelectType={handleSelectType} />
      )}

      {/* Info */}
      <Paper sx={{ p: 3, mt: 4, bgcolor: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Comment ça fonctionne ?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Chaque type de projet définit ses propres catégories de sections (ex: Pages, Composants pour Dev; Audit, Pentest pour Cyber), 
          ses types d'heures (Frontend/Backend pour Dev; Installation/Configuration pour VoIP), 
          son catalogue de produits/matériel et ses types de coûts récurrents. 
          Lors de la création d'un devis, le type de projet détermine les options disponibles.
        </Typography>
      </Paper>
    </Box>
  );
}

export default ProjectTypeSettings;
